import { type CSSProperties, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qHomeCollectionsDetail, type HomeCollectionsDetailRow } from '@helios/data-access';
import { InvoicesTab } from '../patient/consultation/InvoicesTab';
import {
  DrawerSummaryBanner,
  DrawerRowCard,
  DrawerFooterButton,
  DrawerEmptyState,
  PatientAvatar,
  StatusBadge,
  RowCTAButton,
  fmtDrawerTime,
  paymentMethodLabel,
} from './shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';

export interface CollectionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayStartUtc: string;
  dayEndUtc: string;
  portalStyle?: CSSProperties;
}

export function CollectionsDrawer({
  open,
  onOpenChange,
  dayStartUtc,
  dayEndUtc,
  portalStyle,
}: CollectionsDrawerProps) {
  const navigate = useNavigate();
  const fmtCompact = useFormatMoney({ compact: true });
  const fmtCurrency = (amount: number) => fmtCompact(Math.round(amount));
  const { data: rows = [] } = usePowerSyncQuery<HomeCollectionsDetailRow>(
    qHomeCollectionsDetail(dayStartUtc, dayEndUtc),
  );

  // Invoice opened in-place (overlaying this drawer) when a row is clicked.
  const [openInvoice, setOpenInvoice] = useState<{
    patientId: string;
    invoiceId: string;
    invoiceNo: string | null;
  } | null>(null);

  const totalCollected = useMemo(
    () => rows.reduce((sum, r) => sum + (r.grand_total ?? 0), 0),
    [rows],
  );

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Collections Today"
        description="Payment transactions"
        width={520}
        portalStyle={portalStyle}
        footer={
          <DrawerFooterButton
            label="Open Billing"
            color="#059669"
            onClick={() => {
              navigate('/clinic/queue');
              onOpenChange(false);
            }}
          />
        }
      >
        <DrawerSummaryBanner
          icon="credit-card"
          iconBg="#ecfdf5"
          iconColor="#059669"
          value={fmtCurrency(totalCollected)}
          description={`${rows.length} payment${rows.length !== 1 ? 's' : ''} received`}
        />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3, 12px)',
            padding: 'var(--sp-4, 16px)',
          }}
        >
          {rows.length === 0 && <DrawerEmptyState message="No collections today" />}

          {rows.map((row) => (
            <DrawerRowCard key={row.payment_id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
                <PatientAvatar name={row.patient_name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm, 13px)',
                      fontWeight: 'var(--font-weight-semi, 600)',
                      color: 'var(--text, #111827)',
                    }}
                  >
                    {row.patient_name ?? 'Unknown'}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs, 12px)',
                      color: 'var(--text-muted, #6b7280)',
                    }}
                  >
                    {fmtDrawerTime(row.updated_at)} • {row.invoice_no ?? '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base, 14px)',
                      fontWeight: 'var(--font-weight-semi, 600)',
                      color: 'var(--text, #111827)',
                    }}
                  >
                    {fmtCurrency(row.grand_total)}
                  </div>
                  <StatusBadge label={paymentMethodLabel(row.payment_method)} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--sp-2, 8px)' }}>
                <RowCTAButton
                  label="Open Patient"
                  variant="outline"
                  onClick={() => {
                    if (!row.patient_id) return;
                    navigate(`/patient/${row.patient_id}/encounters`);
                    onOpenChange(false);
                  }}
                />
                {row.invoice_id && row.patient_id && (
                  <RowCTAButton
                    label="Open Invoice"
                    variant="filled"
                    color="#059669"
                    onClick={() =>
                      setOpenInvoice({
                        patientId: row.patient_id!,
                        invoiceId: row.invoice_id!,
                        invoiceNo: row.invoice_no,
                      })
                    }
                  />
                )}
              </div>
            </DrawerRowCard>
          ))}
        </div>
      </Drawer>

      {/* Invoice opened in place — overlays the Collections drawer. */}
      <Drawer
        open={!!openInvoice}
        onOpenChange={(o) => !o && setOpenInvoice(null)}
        title={openInvoice ? `Invoice ${openInvoice.invoiceNo ?? ''}`.trim() : 'Invoice'}
        width="min(1100px, 96vw)"
        portalStyle={portalStyle}
      >
        {openInvoice && (
          <div style={{ padding: 'var(--sp-4)' }}>
            <InvoicesTab
              key={openInvoice.invoiceId}
              patientId={openInvoice.patientId}
              initialInvoiceId={openInvoice.invoiceId}
              singlePane
              hideHeader
            />
          </div>
        )}
      </Drawer>
    </>
  );
}
